import {Box, Text, themeGet, type SxProp} from '@primer/react'
import {format, isAfter, isBefore, isEqual, isToday, isWeekend} from 'date-fns'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import styled from 'styled-components'
import {isMacOS} from '@github-ui/get-os'
import {testIdProps} from '@github-ui/test-id-props'
import {type DaySelection, isMultiSelection, isRangeSelection} from '../types'
import {useDatePickerContext} from './Provider'

export type DayProps = {
  disabled?: boolean
  focused?: boolean
  onAction?: (date: Date, event?: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => void
  selected?: DaySelection
  date: Date
} & SxProp

const DayBaseComponent = styled(Box)`
  align-content: center;
  display: flex;
  justify-content: center;
  min-width: 38px;
  min-height: 38px;
  padding: ${themeGet('space.1')};
`

const states = {
  disabled: {
    background: themeGet('colors.canvas.primary'),
    borderRadius: themeGet('radii.2'),
    color: themeGet('colors.fg.subtle'),
    todayColor: themeGet('colors.fg.subtle'),
  },
  selected: {
    default: {
      background: themeGet('colors.accent.emphasis'),
      borderRadius: themeGet('radii.2'),
      color: themeGet('colors.fg.onEmphasis'),
      todayColor: themeGet('colors.fg.onEmphasis'),
    },
    startInactive: {
      background: themeGet('colors.accent.muted'),
      borderRadius: '4px 0 0 4px',
      color: themeGet('colors.fg.default'),
      todayColor: themeGet('colors.fg.default'),
    },
    start: {
      background: themeGet('colors.accent.emphasis'),
      borderRadius: '4px 0 0 4px',
      color: themeGet('colors.fg.onEmphasis'),
      todayColor: themeGet('colors.fg.onEmphasis'),
    },
    middle: {
      background: themeGet('colors.accent.subtle'),
      borderRadius: '0',
      color: themeGet('colors.fg.default'),
      todayColor: themeGet('colors.accent.fg'),
    },
    end: {
      background: themeGet('colors.accent.emphasis'),
      borderRadius: '0 4px 4px 0',
      color: themeGet('colors.fg.onEmphasis'),
      todayColor: themeGet('colors.fg.onEmphasis'),
    },
    endInactive: {
      background: themeGet('colors.accent.muted'),
      borderRadius: '0 4px 4px 0',
      color: themeGet('colors.fg.default'),
      todayColor: themeGet('colors.fg.default'),
    },
  },
  default: {
    normal: {
      background: themeGet('colors.canvas.primary'),
      borderRadius: themeGet('radii.2'),
      color: themeGet('colors.fg.default'),
      todayColor: themeGet('colors.accent.fg'),
    },
    hover: {
      background: themeGet('colors.neutral.muted'),
      borderRadius: themeGet('radii.2'),
      color: themeGet('colors.fg.default'),
      todayColor: themeGet('colors.accent.fg'),
    },
    pressed: {
      background: themeGet('colors.neutral.emphasis'),
      borderRadius: themeGet('radii.2'),
      color: themeGet('colors.fg.onEmphasis'),
      todayColor: themeGet('colors.fg.onEmphasis'),
    },
  },
}

type DayComponentProps = {today?: boolean; range?: boolean} & Omit<DayProps, 'date'>

const getStateStyles = (
  props: DayComponentProps,
  prop: 'background' | 'color' | 'borderRadius',
  state: 'normal' | 'hover' | 'pressed',
) => {
  const {disabled, selected, today} = props
  const prop_ = today && prop === 'color' ? 'todayColor' : prop
  if (selected) {
    switch (selected) {
      case 'start':
        return states.selected.start[prop_]
      case 'startInactive':
        return states.selected.startInactive[prop_]
      case 'middle':
        return states.selected.middle[prop_]
      case 'end':
        return states.selected.end[prop_]
      case 'endInactive':
        return states.selected.endInactive[prop_]
      default:
        return states.selected.default[prop_]
    }
  } else if (disabled) {
    return states.disabled[prop_]
  } else {
    return states.default[state][prop_]
  }
}

const DayComponent = styled(DayBaseComponent).attrs((props: DayComponentProps) => ({
  background: getStateStyles(props, 'background', 'normal'),
  borderRadius: getStateStyles(props, 'borderRadius', 'normal'),
  textColor: getStateStyles(props, 'color', 'normal'),
  backgroundHover: getStateStyles(props, 'background', 'hover'),
  textColorHover: getStateStyles(props, 'color', 'hover'),
  backgroundPressed: getStateStyles(props, 'background', 'pressed'),
  textColorPressed: getStateStyles(props, 'color', 'pressed'),
}))<DayComponentProps>`
  background-color: ${props => props.background};
  ${props =>
    !props.range
      ? `margin: 1px`
      : `margin-top: 1px;
        margin-bottom: 1px`};
  border-radius: ${props => props.borderRadius};
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  transition: 0.1s background-color ease;

  & ${Text} {
    align-self: center;
    color: ${props => props.textColor};
    display: flex;
    font-family: ${themeGet('fonts.mono')};
    font-weight: ${props => (props.today ? 'bold' : 'normal')};
    font-size: ${themeGet('fontSizes.0')};
    justify-content: center;
    justify-self: center;
    padding: 4px 0;
    position: relative;
    transition: 0.1s color ease;
    user-select: none;
    width: 16px;

    &:after {
      content: '';
      display: ${props => (props.today ? 'block' : 'none')};
      position: absolute;
      bottom: 0;
      background: ${props =>
        props.selected && props.selected !== 'middle'
          ? themeGet('colors.fg.onEmphasis')(props)
          : themeGet('colors.accent.emphasis')(props)};
      width: 100%;
      height: 2px;
      border-radius: 1px;
    }
  }

  &:hover {
    background-color: ${props => props.backgroundHover};
    transition: 0.05s background-color ease;
    & ${Text} {
      color: ${props => props.textColorHover};
      transition: 0.1s color ease;
    }
  }

  &:focus-visible {
    outline: none;
    /* Make sure the shadow shows above next date background: */
    z-index: 1;
    box-shadow: 0 0 0 2px ${themeGet('colors.accent.fg')};
  }

  &:active {
    background-color: ${props => props.backgroundPressed};
    box-shadow: inset ${themeGet('shadows.shadow.medium')};
    transition:
      0.1s background-color ease,
      0.1s box-shadow ease,
      0.1s color ease;

    & ${Text} {
      color: ${props => props.textColorPressed};
      transition: 0.1s color ease;
    }
  }
`

export const Day = ({date, onAction}: DayProps) => {
  const {
    configuration: {disableWeekends, minDate, maxDate, variant, showInputs},
    hoverRange,
    selection,
    onDateHover,
    onDateSelection,
    activeRangeEnd,
  } = useDatePickerContext()
  const dayRef = useRef<HTMLDivElement>(null)

  const [selected, setSelected] = useState<DaySelection>(false)
  const today = isToday(date)

  // Determine if the given date is inside of the hover range
  useEffect(() => {
    if (!hoverRange || !isRangeSelection(hoverRange)) return

    if (isEqual(date, hoverRange.from)) {
      setSelected(activeRangeEnd === 'from' ? 'start' : 'startInactive')
    } else if (hoverRange.to && isEqual(date, hoverRange.to)) {
      setSelected(activeRangeEnd === 'to' ? 'end' : 'endInactive')
    } else if (isAfter(date, hoverRange.from) && hoverRange.to && isBefore(date, hoverRange.to)) {
      setSelected('middle')
    } else {
      setSelected(false)
    }
  }, [date, hoverRange, today, activeRangeEnd])

  // Determine if the given date is selected
  useEffect(() => {
    if (!selection) {
      setSelected(false)
      return
    }

    if (isMultiSelection(selection)) {
      setSelected(!!selection.find(d => isEqual(d, date)))
    } else if (isRangeSelection(selection)) {
      if (isEqual(date, selection.from) && selection.to && isEqual(selection.from, selection.to)) {
        setSelected(true)
      } else if (isEqual(date, selection.from)) {
        setSelected(activeRangeEnd === 'from' ? 'start' : 'startInactive')
      } else if (selection.to && isEqual(date, selection.to)) {
        setSelected(activeRangeEnd === 'to' ? 'end' : 'endInactive')
      } else if (isAfter(date, selection.from) && selection.to && isBefore(date, selection.to)) {
        setSelected('middle')
      } else {
        setSelected(false)
      }
    } else if (isEqual(date, selection) !== selected) {
      setSelected(isEqual(date, selection))
    }

    // We do not want to subscribe to selected
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, selection, today, activeRangeEnd])

  let disabled = false

  // Check if date should be disabled
  if (minDate || maxDate || disableWeekends) {
    disabled =
      (minDate ? isBefore(date, minDate) : false) ||
      (maxDate ? isAfter(date, maxDate) : false) ||
      (disableWeekends ? isWeekend(date) : false)
  }

  const mouseDownHandler = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      // Prevent focus from leaving the inputs
      if (showInputs) event.preventDefault()
    },
    [showInputs],
  )

  const actionHandler = useCallback(
    (event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) {
        return
      }

      const modifiers = {range: event.shiftKey, multiple: isMacOS() ? event.metaKey : event.ctrlKey}
      if ('key' in event) {
        if ([' ', 'Enter'].includes(event.key)) {
          onDateSelection(date, modifiers, true, 'submit-key-press')
          onAction?.(date, event)
        }
      } else {
        onDateSelection(date, modifiers)
        onAction?.(date, event)
      }
    },
    [disabled, onDateSelection, onAction, date],
  )

  const label = useMemo(() => {
    const todayLabel = today ? ' (Today)' : ''
    // No need for selected label if not range because aria-selected will already indicate boolean state
    const selectedLabel =
      selected === 'start'
        ? ' (Start of selected range)'
        : selected === 'end'
        ? ' (End of selected range)'
        : selected === 'middle'
        ? ' (Inside selected range)'
        : ''
    const minMaxLabel =
      minDate && isEqual(date, minDate)
        ? ' (Minimum allowed date)'
        : maxDate && isEqual(date, maxDate)
        ? ' (Maximum allowed date)'
        : ''
    return `${format(date, 'EEEE, MMMM d')}${todayLabel}${minMaxLabel}${selectedLabel}`
  }, [today, date, selected, minDate, maxDate])

  return (
    <DayComponent
      aria-disabled={disabled}
      aria-selected={selected !== false}
      aria-label={label}
      data-date={format(date, 'MM/dd/yyyy')}
      data-disabled={disabled ? 'true' : undefined}
      {...testIdProps(`day-${format(date, 'MM/dd/yyyy')}`)}
      disabled={disabled}
      onClick={actionHandler}
      onMouseDown={mouseDownHandler}
      onKeyPress={actionHandler}
      onMouseEnter={() => onDateHover(date)}
      ref={dayRef}
      range={variant === 'range'}
      role="gridcell"
      selected={selected}
      tabIndex={-1}
      today={today}
    >
      <Text>{date.getDate()}</Text>
    </DayComponent>
  )
}

export const BlankDay = styled(DayBaseComponent)`
  background-color: ${themeGet('colors.canvas.primary')};
`

try{ DayBaseComponent.displayName ||= 'DayBaseComponent' } catch {}
try{ DayComponent.displayName ||= 'DayComponent' } catch {}
try{ Day.displayName ||= 'Day' } catch {}
try{ BlankDay.displayName ||= 'BlankDay' } catch {}